/**
 * MON JOURNAL — écran enfant multi-format : texte, dessin (tactile), audio, photo,
 * vidéo. Visibilité TOTALE côté Dashboard Parent (demande explicite de Pat) — ce
 * n'est pas un espace privé, l'enfant le sait via le sous-titre de l'écran.
 */
import { useCallback, useEffect, useRef, useState } from "react";

type Item = { id: string; type: string; caption: string; at: number; text: string | null; url: string | null };
type View = "picker" | "text" | "drawing" | "audio" | "mine";

const TYPE_LABEL: Record<string, string> = {
  text: "✍️ Texte", drawing: "🎨 Dessin", audio: "🎤 Audio", photo: "📸 Photo", video: "🎥 Vidéo",
};

export default function Journal({ apiBase, onClose }: { apiBase: string; onClose: () => void }) {
  const [view, setView] = useState<View>("picker");
  const [saved, setSaved] = useState(false);
  const photoRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLInputElement | null>(null);

  const upload = useCallback(async (form: FormData) => {
    try {
      await fetch(`${apiBase}/api/child/journal`, { method: "POST", body: form });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
      setView("picker");
    } catch { /* offline — tant pis pour cette entrée */ }
  }, [apiBase]);

  const onCapture = (type: "photo" | "video") => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const form = new FormData();
    form.append("type", type);
    form.append("file", file);
    await upload(form);
  };

  return (
    <div style={S.screen}>
      <div style={S.header}>
        <button style={S.close} onClick={onClose} aria-label="Fermer">✕</button>
        <div style={{ flex: 1 }}>
          <div style={S.title}>📔 MON JOURNAL</div>
          <div style={S.sub}>Ton papa peut voir tout ce que tu mets ici</div>
        </div>
        {view !== "picker" && <button style={S.back} onClick={() => setView("picker")}>← Retour</button>}
      </div>

      {view === "picker" && (
        <div style={S.grid}>
          <button style={S.tile} onClick={() => setView("text")}>✍️<span style={{ fontSize: 13, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.04em" }}>Texte</span></button>
          <button style={S.tile} onClick={() => setView("drawing")}>🎨<span style={{ fontSize: 13, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.04em" }}>Dessin</span></button>
          <button style={S.tile} onClick={() => setView("audio")}>🎤<span style={{ fontSize: 13, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.04em" }}>Audio</span></button>
          <button style={S.tile} onClick={() => photoRef.current?.click()}>📸<span style={{ fontSize: 13, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.04em" }}>Photo</span></button>
          <button style={S.tile} onClick={() => videoRef.current?.click()}>🎥<span style={{ fontSize: 13, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.04em" }}>Vidéo</span></button>
          <button style={S.tile} onClick={() => setView("mine")}>📖<span style={{ fontSize: 13, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.04em" }}>Mon journal</span></button>
        </div>
      )}
      <input ref={photoRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={onCapture("photo")} />
      <input ref={videoRef} type="file" accept="video/*" capture="environment" style={{ display: "none" }} onChange={onCapture("video")} />

      {view === "text" && <TextEntry onSave={upload} />}
      {view === "drawing" && <DrawingEntry onSave={upload} />}
      {view === "audio" && <AudioEntry onSave={upload} />}
      {view === "mine" && <MyEntries apiBase={apiBase} />}

      {saved && <div style={S.toast}>✅ Enregistré dans ton journal !</div>}
    </div>
  );
}

function TextEntry({ onSave }: { onSave: (form: FormData) => Promise<void> }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    const form = new FormData();
    form.append("type", "text");
    form.append("text", text.trim());
    await onSave(form);
    setBusy(false);
    setText("");
  };
  return (
    <div style={S.panel}>
      <textarea
        style={S.textarea}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Écris ce que tu veux garder..."
        maxLength={2000}
        autoFocus
      />
      <button style={S.saveBtn} onClick={save} disabled={!text.trim() || busy}>💾 Enregistrer</button>
    </div>
  );
}

function DrawingEntry({ onSave }: { onSave: (form: FormData) => Promise<void> }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [busy, setBusy] = useState(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const rect = cvs.getBoundingClientRect();
    cvs.width = rect.width * dpr;
    cvs.height = rect.height * dpr;
    const ctx = cvs.getContext("2d")!;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1a1a1a";
  }, []);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    setEmpty(false);
    const ctx = canvasRef.current!.getContext("2d")!;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext("2d")!;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const cvs = canvasRef.current!;
    const ctx = cvs.getContext("2d")!;
    const rect = cvs.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setEmpty(true);
  };

  const save = async () => {
    if (empty || busy) return;
    setBusy(true);
    const blob: Blob | null = await new Promise((resolve) => canvasRef.current!.toBlob((b) => resolve(b), "image/png"));
    if (blob) {
      const form = new FormData();
      form.append("type", "drawing");
      form.append("file", blob, "dessin.png");
      await onSave(form);
    }
    setBusy(false);
  };

  return (
    <div style={S.panel}>
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: "100%", borderRadius: 16, touchAction: "none", background: "#fff" }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
        <button style={S.leaveBtn} onClick={clear}>🗑 Effacer</button>
        <button style={{ ...S.saveBtn, flex: 1, marginTop: 0 }} onClick={save} disabled={empty || busy}>💾 Enregistrer</button>
      </div>
    </div>
  );
}

function AudioEntry({ onSave }: { onSave: (form: FormData) => Promise<void> }) {
  const [status, setStatus] = useState<"idle" | "recording" | "ready">("idle");
  const [busy, setBusy] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const blobRef = useRef<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        blobRef.current = blob;
        setUrl(URL.createObjectURL(blob));
        setStatus("ready");
      };
      rec.start();
      recRef.current = rec;
      setStatus("recording");
    } catch { /* micro refusé/indisponible */ }
  };
  const stop = () => recRef.current?.stop();
  const redo = () => { setUrl(null); blobRef.current = null; setStatus("idle"); };

  const save = async () => {
    if (!blobRef.current || busy) return;
    setBusy(true);
    const form = new FormData();
    form.append("type", "audio");
    form.append("file", blobRef.current, "audio.webm");
    await onSave(form);
    setBusy(false);
  };

  return (
    <div style={{ ...S.panel, alignItems: "center", justifyContent: "center", display: "flex", flexDirection: "column" }}>
      {status === "idle" && (
        <button style={S.micBtn} onClick={start}>🎤<span style={{ fontSize: 13, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.04em" }}>Appuie pour enregistrer</span></button>
      )}
      {status === "recording" && (
        <button style={{ ...S.micBtn, background: "#ff6b6b33", borderColor: "#ff6b6b" }} onClick={stop}>⏺<span style={{ fontSize: 13, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.04em" }}>Enregistrement... appuie pour arrêter</span></button>
      )}
      {status === "ready" && url && (
        <div style={{ width: "100%", textAlign: "center" }}>
          <audio src={url} controls style={{ width: "100%", marginBottom: 16 }} />
          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.leaveBtn} onClick={redo}>🔄 Refaire</button>
            <button style={{ ...S.saveBtn, flex: 1, marginTop: 0 }} onClick={save} disabled={busy}>💾 Enregistrer</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MyEntries({ apiBase }: { apiBase: string }) {
  const [items, setItems] = useState<Item[]>([]);
  useEffect(() => {
    fetch(`${apiBase}/api/child/journal`).then((r) => r.json()).then((d) => setItems(d.items || [])).catch(() => {});
  }, [apiBase]);
  if (!items.length) return <div style={{ ...S.panel, color: "var(--kid-text-3)", textAlign: "center", paddingTop: 30 }}>Rien pour l'instant — crée ta première entrée !</div>;
  return (
    <div style={{ ...S.panel, overflowY: "auto" }}>
      {items.map((it) => (
        <div key={it.id} style={S.entryCard}>
          <div style={S.entryHead}>{TYPE_LABEL[it.type] || it.type} · {new Date(it.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
          {it.type === "text" && <div style={S.entryText}>{it.text}</div>}
          {(it.type === "drawing" || it.type === "photo") && it.url && <img src={it.url} alt="" style={S.entryMedia} />}
          {it.type === "audio" && it.url && <audio src={it.url} controls style={{ width: "100%" }} />}
          {it.type === "video" && it.url && <video src={it.url} controls style={S.entryMedia} />}
        </div>
      ))}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  screen: { position: "fixed", inset: 0, zIndex: 62, background: "var(--kid-bg)", display: "flex", flexDirection: "column", color: "var(--kid-text)", fontFamily: "var(--kid-font-sans, system-ui)" },
  header: { display: "flex", alignItems: "center", gap: 10, padding: "16px 16px 8px", borderBottom: "1px solid var(--kid-border)" },
  close: { width: 40, height: 40, borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-cyan)", fontSize: 18, cursor: "pointer" },
  title: { fontFamily: "var(--kid-font-mono)", letterSpacing: "0.1em", fontSize: 14, color: "var(--kid-cyan)" },
  sub: { fontSize: 11, color: "var(--kid-text-3)", marginTop: 2 },
  back: { border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-cyan)", borderRadius: 999, padding: "8px 14px", fontSize: 12, cursor: "pointer" },
  grid: { flex: 1, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14, padding: 20, alignContent: "center" },
  tile: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, aspectRatio: "1.3", borderRadius: 20, border: "1px solid var(--kid-cyan-40)", background: "rgba(255,255,255,0.04)", color: "var(--kid-text)", fontSize: 34, cursor: "pointer" },
  panel: { flex: 1, display: "flex", flexDirection: "column", padding: 16 },
  textarea: { flex: 1, resize: "none", borderRadius: 16, border: "1px solid var(--kid-cyan-40)", background: "rgba(255,255,255,0.04)", color: "var(--kid-text)", padding: 14, fontSize: 16, fontFamily: "inherit" },
  saveBtn: { marginTop: 12, padding: "14px", borderRadius: 14, border: "none", background: "linear-gradient(180deg, var(--kid-cyan), #008cbe)", color: "#04222c", fontSize: 16, fontWeight: 800, cursor: "pointer" },
  leaveBtn: { padding: "14px 16px", borderRadius: 14, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-text-2)", fontSize: 14, cursor: "pointer" },
  micBtn: { display: "flex", flexDirection: "column", alignItems: "center", gap: 12, fontSize: 60, background: "rgba(255,255,255,0.04)", border: "2px solid var(--kid-cyan-40)", borderRadius: 24, padding: "40px 30px", color: "var(--kid-text)", cursor: "pointer" },
  toast: { position: "absolute", top: "14%", left: "50%", transform: "translateX(-50%)", background: "rgba(6,9,15,0.9)", border: "1px solid var(--kid-cyan)", color: "var(--kid-cyan)", padding: "10px 18px", borderRadius: 999, fontSize: 14, fontWeight: 700, zIndex: 65 },
  entryCard: { border: "1px solid var(--kid-cyan-40)", borderRadius: 14, padding: 12, marginBottom: 10 },
  entryHead: { fontSize: 11, color: "var(--kid-text-3)", fontFamily: "var(--kid-font-mono)", marginBottom: 6 },
  entryText: { fontSize: 15, lineHeight: 1.4, whiteSpace: "pre-wrap" },
  entryMedia: { width: "100%", borderRadius: 10, display: "block" },
};
